import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Input,
  InputNumber,
  Button,
  message,
  Upload,
  Modal,
  Card,
  Space,
  Row,
  Col,
} from 'antd';
import ImageDescriptionDetailModal from './ImageDescriptionDetailModal';
import { FileSearchOutlined, UploadOutlined, CloseCircleOutlined } from '@ant-design/icons';

import { useSocketContext } from '../../../sockets/SocketContext';
import {
  setLastSearchQuery,
  setFormValues,
  setLastDescriptionSearchImageDataUrl,
} from '../../../store/tz/reducer';
import { resetImageDescription } from '../../../store/imageDescriptionSlice';
import MktuClassSelector from '../../common/MktuClassSelector';
import normalizeMktu from '../../../utils/normalizeMktu';
import { preprocessImage, getCompressedBase64, MAX_INPUT_SIZE_BYTES } from '../../../utils/imagePreprocess';
import ImageCropper from '../ext/ImageCropper';

const { TextArea } = Input;

const SearchForm = ({ setLocalState }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const savedValues = useSelector((s) => s.tz.forms.description);
  const { createNewTaskOnServer, getImageDescription } = useSocketContext();
  const imageDescriptionLoading = useSelector((s) => s.imageDescription.loading);
  const imageDescriptionData = useSelector((s) => s.imageDescription.result);
  const [detailModalVisible, setDetailModalVisible] = React.useState(false);

  const [previewSrc, setPreviewSrc] = React.useState(null);
  const [originalPreviewSrc, setOriginalPreviewSrc] = React.useState(null);
  const [cropVisible, setCropVisible] = React.useState(false);
  const [croppedBlob, setCroppedBlob] = React.useState(null);
  const [cropConfig, setCropConfig] = React.useState(null);
  const [fileName, setFileName] = React.useState(null);

  useEffect(() => {
    dispatch(setLastDescriptionSearchImageDataUrl(previewSrc || null));
  }, [previewSrc, dispatch]);

  const syncStateUp = (extra = {}) =>
    setLocalState?.((prev) => ({
      ...prev,
      previewSrc,
      originalPreviewSrc,
      croppedBlob,
      cropConfig,
      fileName,
      ...extra,
    }));

  const handleFileChange = async ({ fileList }) => {
    if (!fileList.length) {
      resetFile();
      return;
    }
    let file = fileList[fileList.length - 1].originFileObj;
    const originalSize = file.size;
    try {
      file = await preprocessImage(file);
      if (file.size < originalSize) {
        message.info('Изображение сжато до допустимого размера');
      }
    } catch (err) {
      message.error(err?.message || 'Ошибка обработки изображения');
      resetFile();
      return;
    }
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataURL = e.target.result;
      setOriginalPreviewSrc(dataURL);
      setPreviewSrc(dataURL);
      setCropVisible(true);
      syncStateUp({ originalPreviewSrc: dataURL, previewSrc: dataURL });
    };
    reader.readAsDataURL(file);
  };

  const resetFile = () => {
    setPreviewSrc(null);
    setOriginalPreviewSrc(null);
    setCroppedBlob(null);
    setCropConfig(null);
    setFileName(null);
    setDetailModalVisible(false);
    dispatch(resetImageDescription());
    setLocalState?.((prev) => ({
      ...prev,
      previewSrc: null,
      originalPreviewSrc: null,
      croppedBlob: null,
      cropConfig: null,
      fileName: null,
    }));
  };

  const handleGetDescription = async () => {
    if (!previewSrc && !croppedBlob) {
      message.error('Сначала загрузите изображение');
      return;
    }
    try {
      const base64 = await getCompressedBase64(croppedBlob || previewSrc);
      getImageDescription(base64, (response) => {
        if (response?.status === 'ok' && response?.description) {
          const desc = response.description;
          const semanticText =
            typeof desc === 'string' ? desc : (desc?.semantic_description ?? '');
          form.setFieldsValue({ description: semanticText });
          dispatch(setFormValues({ formKey: 'description', values: { description: semanticText } }));
          message.success('Описание получено и вставлено в поле');
        } else {
          message.error(response?.message || 'Не удалось получить описание');
        }
      });
    } catch (err) {
      message.error(err?.message || 'Ошибка обработки изображения');
    }
  };


  const handleFinish = async (values) => {
    const { description, quantity, mktu } = values;
    const normalizedMktu = normalizeMktu(mktu);
    const descriptionText = (description || '').trim();

    dispatch(setLastSearchQuery(descriptionText));

    if (setLocalState) {
      setLocalState((prev) => ({
        ...prev,
        searchQuery: descriptionText,
        quantity,
        mktu,
      }));
    }

    const jobs = [
      { desc: { description_tz: descriptionText, quantity, mktu: normalizedMktu }, type: 'description_tz', label: 'ТЗ' },
      { desc: { description_req: descriptionText, quantity, mktu: normalizedMktu }, type: 'description_req', label: 'заявок' },
      { desc: { description_wipo: descriptionText, quantity, mktu: normalizedMktu }, type: 'description_wipo', label: 'WIPO' },
    ];

    const results = await Promise.all(
      jobs.map((j) => new Promise((resolve) => {
        createNewTaskOnServer(j.desc, j.type, (err) => resolve({ label: j.label, err }));
      })),
    );
    const failed = results.filter((r) => r.err);
    if (failed.length === 0) {
      message.success('Все запросы отправлены');
    } else {
      failed.forEach((r) => message.error(`Не удалось создать задачу ${r.label}`));
    }
  };

  useEffect(() => {
    form.setFieldsValue(savedValues);
  }, [form, savedValues]);

  return (
    <>
      <Card
        className="tz-description-search-card"
        title="Поиск по содержанию"
        bordered
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          onValuesChange={(_, all) => dispatch(setFormValues({ formKey: 'description', values: all }))}
          className="tz-description-search-form"
        >
          <Form.Item label="Изображение">
            <Space align="start" wrap size="middle" className="tz-description-search__image-row">
              <Upload
                beforeUpload={(file) => {
                  if (file.size > MAX_INPUT_SIZE_BYTES) {
                    message.error(`Файл слишком большой (макс. ${MAX_INPUT_SIZE_BYTES / 1024 / 1024} MB)`);
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                onChange={handleFileChange}
                onRemove={resetFile}
                maxCount={1}
                accept="image/*"
                listType="picture-card"
                showUploadList={false}
              >
                {!previewSrc && (
                  <div>
                    <UploadOutlined style={{ fontSize: 24 }} />
                    <div style={{ marginTop: 8 }}>Выбрать</div>
                  </div>
                )}
              </Upload>

              {previewSrc && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={previewSrc}
                    alt="Предпросмотр"
                    onClick={() => setCropVisible(true)}
                    style={{
                      maxWidth: 104,
                      maxHeight: 104,
                      objectFit: 'contain',
                      border: '1px solid #d9d9d9',
                      borderRadius: 6,
                      cursor: 'pointer',
                      display: 'block',
                      background: 'var(--theme-preview-bg)',
                    }}
                  />
                  <CloseCircleOutlined
                    style={{
                      fontSize: 22,
                      color: '#ff4d4f',
                      cursor: 'pointer',
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      background: 'var(--theme-surface)',
                      borderRadius: '50%',
                      boxShadow: '0 0 2px #ccc',
                    }}
                    title="Убрать изображение"
                    onClick={resetFile}
                  />
                </div>
              )}

              <Space direction="vertical" size="small" style={{ alignSelf: 'center' }}>
                <Button
                  type="default"
                  onClick={handleGetDescription}
                  loading={imageDescriptionLoading}
                  disabled={!previewSrc && !croppedBlob}
                >
                  Получить описание
                </Button>
                {imageDescriptionData && (
                  <Button type="link" size="small" onClick={() => setDetailModalVisible(true)} style={{ padding: 0 }}>
                    Показать доп. описание
                  </Button>
                )}
              </Space>
            </Space>
          </Form.Item>

          <Form.Item
            name="description"
            label="Описание ТЗ"
            rules={[{ required: true, message: 'Введите текстовое описание' }]}
          >
            <TextArea
              className="tz-description-search__textarea"
              placeholder="Введите текстовое описание или загрузите изображение и нажмите «Получить описание»"
              autoSize={{ minRows: 5, maxRows: 18 }}
            />
          </Form.Item>

          <Row gutter={[16, 16]} align="bottom" wrap>
            <Col xs={24} md={14} lg={15}>
              <Form.Item name="mktu" label="МКТУ" style={{ marginBottom: 0 }}>
                <MktuClassSelector placeholder="Классы" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={5}>
              <Form.Item name="quantity" label="Количество" initialValue={20} style={{ marginBottom: 0 }}>
                <InputNumber min={1} max={200} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={16} md={5} className="tz-description-search__submit-col">
              <Form.Item label=" " colon={false} style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" icon={<FileSearchOutlined />} block>
                  Найти
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <ImageDescriptionDetailModal
        open={detailModalVisible}
        onClose={() => setDetailModalVisible(false)}
        imageSrc={previewSrc}
        descriptionData={imageDescriptionData}
      />

      <Modal
        open={cropVisible}
        footer={null}
        onCancel={() => setCropVisible(false)}
        width="min(800px, calc(100vw - 32px))"
        centered
      >
        {originalPreviewSrc && (
          <ImageCropper
            src={originalPreviewSrc}
            initialCrop={cropConfig}
            onComplete={(blob, cfg) => {
              setCroppedBlob(blob);
              setCropConfig(cfg);
              const r = new FileReader();
              r.onload = (e) => {
                setPreviewSrc(e.target.result);
                syncStateUp({
                  previewSrc: e.target.result,
                  croppedBlob: blob,
                  cropConfig: cfg,
                });
              };
              r.readAsDataURL(blob);
              setCropVisible(false);
            }}
            onCancel={() => setCropVisible(false)}
          />
        )}
      </Modal>
    </>
  );
};

export default SearchForm;
